import { Card, Col, Empty, Image, Row, Skeleton } from 'antd';
import { useRecoilState } from 'recoil';
import { selectedSupplierDynamicsVendorState } from 'recoil/atoms';
import { useEffect, useMemo, useState } from 'react';
import { useVendorApi } from 'api/vendor';
import fallbackLogo from 'assets/fallbackLogo.png';
import { SupplierCompetitor } from 'models/SupplierDynamics';
import './SupplierCompetitors.scss';
import { SupplierDynamicsVendor } from 'types/global';
import {
  NPI_SUPPLIER_DYNAMICS_TABS,
  normalizeSupplierDynamicsTab,
  SUPPLIER_DYNAMICS_TAB_KEYS
} from 'components/supplier-dynamics/constants';

interface CompetitorProps {
  competitor: SupplierCompetitor;
  onClick: (competitor: any) => void;
}

const cardStyle = { background: '#f7f9f9' };
const noBorder = { borderBottom: '1px solid white' };
const headStyle = { ...cardStyle, ...noBorder };
// Supplier Dynamics Plus / Quantum is currently disabled. Preserve for future re-enable:
// const quantumViewTabs = ['about', 'products', 'news'];

interface SupplierCompetitorsProps {
  toggleValue?: string;
  selectedSupplierCardTab?: string | undefined;
  setViewInfo?: ((value: { showPartnerInfo: boolean; showNPIInfo: boolean; showToggle: boolean }) => void) | undefined;
  vendorsList?: Array<SupplierDynamicsVendor>;
  setSelectedSupplierCardTab?: (value: ((prevState: string) => string) | string) => void;
  /** When false (e.g. embedded in the deal view), don't write the `?source=…&tab=…` deep-link to the URL. Default true. */
  syncTabToUrl?: boolean;
}

const SupplierCompetitors: React.FC<SupplierCompetitorsProps> = ({
  toggleValue,
  selectedSupplierCardTab,
  setViewInfo,
  vendorsList,
  setSelectedSupplierCardTab,
  syncTabToUrl = true
}: SupplierCompetitorsProps): React.ReactElement => {
  // LOCAL STATE
  const [competitors, setCompetitors] = useState<SupplierCompetitor[]>([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState<boolean>(false);

  // RECOIL STATE
  const [selectedVendor, setSelectedVendor] = useRecoilState(selectedSupplierDynamicsVendorState);

  // API
  const { getSupplierCompetitors } = useVendorApi();

  useEffect(() => {
    if (!selectedVendor) return setCompetitors([]);
    (async () => {
      try {
        setLoadingCompetitors(true);
        // ----- BUYER CADDY / Quantum (Partner Info) path commented out: use NPI competitors only -----
        const competitors: SupplierCompetitor[] | undefined =
          // (toggleValue == '' || toggleValue == 'Partner Info') && selectedVendor?.domain
          //   ? await getQuantumSupplierCompetitors(selectedVendor?.domain).then((res) => res?.content)
          //   :
          selectedVendor?.id ? await getSupplierCompetitors(selectedVendor?.id) : undefined;
        setCompetitors(Array.isArray(competitors) ? competitors.slice(0, 10) : []);
        // Supplier Dynamics Plus / Quantum is currently disabled. Preserve this
        // legacy URL branch for future re-enable:
        // if (toggleValue == '' || toggleValue == 'Partner Info') {
        //   const selectedTab =
        //     selectedSupplierCardTab && quantumViewTabs?.includes(selectedSupplierCardTab)
        //       ? selectedSupplierCardTab
        //       : 'about';
        //   window.history.pushState(
        //     null,
        //     '',
        //     `${window.location.pathname}?source=quantum&domain=${selectedVendor?.domain?.replace(
        //       'www.',
        //       ''
        //     )}&tab=${selectedTab}`
        //   );
        //   setSelectedSupplierCardTab && setSelectedSupplierCardTab(selectedTab);
        // } else {
        {
          const normalized = normalizeSupplierDynamicsTab(selectedSupplierCardTab);
          const selectedTab =
            normalized && NPI_SUPPLIER_DYNAMICS_TABS.includes(normalized as (typeof NPI_SUPPLIER_DYNAMICS_TABS)[number])
              ? normalized
              : SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ;
          if (syncTabToUrl) {
            window.history.pushState(
              null,
              '',
              `${window.location.pathname}?source=npi&supplier_id=${selectedVendor.id}&tab=${selectedTab}`
            );
          }
          setSelectedSupplierCardTab && setSelectedSupplierCardTab(selectedTab);
        }
        // }
        setLoadingCompetitors(false);
      } catch (err) {
        console.error('Failed to fetch supplier competitors:', err);
      }
    })();
  }, [selectedVendor, toggleValue]);

  /**
   * Handles the click event for a competitor.
   * @param {any} competitor - The competitor object that was clicked.
   * @returns {void} - This function does not return anything.
   */
  const onClickHandler = (competitor: any): void => {
    const vendor = competitor?.id
      ? { id: competitor.id, domain: competitor.domain }
      : vendorsList?.find(
          (vendor) => vendor.domain?.replace('www.', '')?.toLowerCase() === competitor?.domain?.toLowerCase()
        );

    if (vendor) {
      setViewInfo?.({
        showNPIInfo: false,
        showToggle: true,
        showPartnerInfo: false
      });
    } else {
      setViewInfo?.({
        showNPIInfo: false,
        showToggle: false,
        showPartnerInfo: true
      });
    }
    setSelectedVendor({
      id: competitor?.id || vendor?.id,
      name: competitor.name,
      domain: competitor?.domain
    });
  };

  const isSupplierClickable = (competitor: SupplierCompetitor) => {
    return competitor?.id
      ? competitor?.isClickable
      : !!(['', 'Partner Info', undefined].includes(toggleValue) && selectedVendor?.domain);
  };

  /**
   * Represents a Competitor component.
   * @param {object} CompetitorProps - The props to configure the Competitor component.
   * @param {object} CompetitorProps.competitor - The competitor object containing information about the competitor.
   * @param {Function} CompetitorProps.onClick - The function to be called when the competitor is clicked.
   * @returns {React.ReactElement} - The Competitor component.
   */
  const Competitor = ({ competitor, onClick }: CompetitorProps): React.ReactElement => {
    return useMemo(() => {
      return (
        <Row
          gutter={16}
          style={{ cursor: isSupplierClickable(competitor) ? 'pointer' : 'default' }}
          onClick={() => {
            if (isSupplierClickable(competitor)) {
              onClick(competitor);
            }
          }}
          className={`competitor-wrapper flex-nowrap ${isSupplierClickable(competitor) ? 'active' : ''}`}
        >
          <Col span={6} className={'flex flex-col flex-grow place-content-center'}>
            {(competitor?.logo_url || competitor?.logo) && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 40 }}>
                <Image
                  preview={false}
                  style={{ maxHeight: 34 }}
                  className={'block align-middle'}
                  src={competitor.logo_url || competitor?.logo}
                />
              </div>
            )}
            {!competitor.logo_url && !competitor.logo && (
              <div>
                <Image preview={false} style={{ maxHeight: 40 }} className={'block'} src={fallbackLogo} />
              </div>
            )}
          </Col>
          <Col span={18} className={'flex flex-col flex-grow place-content-center'}>
            <div className="supplier-name">{competitor.name}</div>
          </Col>
        </Row>
      );
    }, [competitor, onClick]);
  };

  return (
    <Card title={'Suppliers with Competing Products'} bodyStyle={cardStyle} headStyle={headStyle}>
      <Skeleton loading={loadingCompetitors}>
        <div className="supplier-competitors">
          {(competitors.length &&
            competitors.map((competitor: SupplierCompetitor) => (
              <Competitor key={competitor.id || competitor.domain} competitor={competitor} onClick={onClickHandler} />
            ))) || (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No competitors currently listed.</div>} />
          )}
        </div>
      </Skeleton>
    </Card>
  );
};

export default SupplierCompetitors;
